import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from 'xlsx';
import { ArrowLeft, Lightning, CircleNotch, Trophy, CheckCircle, Warning, XCircle, Info, Eraser, DownloadSimple } from "@phosphor-icons/react";
import useConsensusGroups from '../hooks/useConsensusGroups';
import { GROUP_STYLE, GroupLegend, ConsensusRefLink, QualityBadge, applyConsensusFilter } from '../components/ConsensusBadge';

const API = process.env.REACT_APP_BACKEND_URL;

const ballConfig = [
  { key: "ball_1", label: "Ball 1", max: 50, color: "bg-[#002FA7]", lightBg: "bg-blue-50", border: "border-blue-200" },
  { key: "ball_2", label: "Ball 2", max: 50, color: "bg-[#002FA7]", lightBg: "bg-blue-50", border: "border-blue-200" },
  { key: "ball_3", label: "Ball 3", max: 50, color: "bg-[#002FA7]", lightBg: "bg-blue-50", border: "border-blue-200" },
  { key: "ball_4", label: "Ball 4", max: 50, color: "bg-[#002FA7]", lightBg: "bg-blue-50", border: "border-blue-200" },
  { key: "ball_5", label: "Ball 5", max: 50, color: "bg-[#002FA7]", lightBg: "bg-blue-50", border: "border-blue-200" },
  { key: "ls_1", label: "Lucky Star 1", max: 12, color: "bg-amber-500", lightBg: "bg-amber-50", border: "border-amber-200" },
  { key: "ls_2", label: "Lucky Star 2", max: 12, color: "bg-amber-500", lightBg: "bg-amber-50", border: "border-amber-200" },
];

const statusIcon = { pass: CheckCircle, warning: Warning, fail: XCircle, info: Info };
const statusColor = { pass: "text-green-600", warning: "text-amber-500", fail: "text-red-500", info: "text-blue-400" };
const verdictStyle = {
  EXCELLENT: "from-green-500 to-emerald-600",
  GOOD: "from-blue-500 to-cyan-600",
  FAIR: "from-amber-500 to-orange-500",
  POOR: "from-red-500 to-rose-600",
};

export default function PredictionMaker() {
  const navigate = useNavigate();
  const [inputs, setInputs] = useState(() => {
    const init = {};
    ballConfig.forEach(b => { init[b.key] = Array(10).fill(""); });
    return init;
  });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [consensusMode, setConsensusMode] = useState("MAX_1_RED"); // default: MAX_1_RED
  const consensus = useConsensusGroups();

  const handleChange = (ballKey, idx, value) => {
    const cleaned = value.replace(/[^0-9]/g, "");
    setInputs(prev => {
      const arr = [...prev[ballKey]];
      arr[idx] = cleaned;
      return { ...prev, [ballKey]: arr };
    });
  };

  const handleClear = (ballKey) => {
    setInputs(prev => ({ ...prev, [ballKey]: Array(10).fill("") }));
  };

  const handleClearAll = () => {
    const init = {};
    ballConfig.forEach(b => { init[b.key] = Array(10).fill(""); });
    setInputs(init);
    setResults(null);
    setError(null);
  };

  const handleSubmit = async () => {
    setError(null);
    const payload = {};
    for (const b of ballConfig) {
      const nums = inputs[b.key].map(v => parseInt(v)).filter(n => !isNaN(n) && n >= 1 && n <= b.max);
      if (nums.length === 0) {
        setError(`${b.label}: Enter at least 1 number`);
        return;
      }
      const unique = [...new Set(nums)];
      payload[b.key] = unique;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/analysis/prediction-maker`, payload);
      setResults(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to generate combinations");
    } finally {
      setLoading(false);
    }
  };

  const filledCount = (ballKey) => inputs[ballKey].filter(v => v !== "").length;

  const handleExportExcel = () => {
    const combos = results?.combinations || [];
    if (combos.length === 0) return;
    // Only ticket data — no scores, no checks, no metadata
    const rows = [
      ["Rank", "Ball 1", "Ball 2", "Ball 3", "Ball 4", "Ball 5", "Lucky Star 1", "Lucky Star 2"],
      ...combos.map((c, i) => [i + 1, ...c.balls, ...c.lucky_stars]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 6 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 12 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tickets");
    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `W49_Prediction_Maker_Tickets_${today}.xlsx`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-testid="back-btn">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">W49: Prediction Maker</h1>
            <p className="text-xs text-gray-500">Enter your candidate numbers per ball position — get up to 200 best combinations (excludes any 5-ball match with a past draw)</p>
          </div>
          <div className="ml-auto flex gap-2">
            <button onClick={handleClearAll} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors" data-testid="clear-all-btn">
              <Eraser size={14} /> Clear All
            </button>
            <button
              onClick={handleExportExcel}
              disabled={!results || (results.combinations || []).length === 0}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="export-tickets-btn"
              title="Download all generated tickets as Excel (only balls + lucky stars — no scores)"
            >
              <DownloadSimple size={14} weight="bold" /> Download Tickets (Excel)
            </button>
            <button onClick={handleSubmit} disabled={loading} className="flex items-center gap-1.5 px-5 py-2 text-sm font-bold text-white bg-[#002FA7] rounded-lg hover:bg-[#001f6f] transition-colors disabled:opacity-50" data-testid="generate-btn">
              {loading ? <CircleNotch size={16} className="animate-spin" /> : <Lightning size={16} weight="fill" />}
              {loading ? "Generating..." : "Generate Best Combos"}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 text-sm font-medium" data-testid="error-msg">{error}</div>
        )}

        {/* Input Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4" data-testid="input-grid">
          {ballConfig.map(b => (
            <div key={b.key} className={`${b.lightBg} ${b.border} border rounded-lg p-3`} data-testid={`input-${b.key}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex w-7 h-7 rounded-full ${b.color} text-white items-center justify-center font-mono font-bold text-xs`}>
                    {b.label.includes("Lucky") ? "LS" : b.label.split(" ")[1]}
                  </span>
                  <span className="font-bold text-sm text-gray-800">{b.label}</span>
                </div>
                <button onClick={() => handleClear(b.key)} className="text-[10px] text-gray-400 hover:text-red-500 transition-colors" data-testid={`clear-${b.key}`}>
                  Clear
                </button>
              </div>
              <div className="text-[10px] text-gray-400 mb-1.5">1-{b.max} • {filledCount(b.key)}/10 filled</div>
              <div className="grid grid-cols-5 gap-1.5">
                {inputs[b.key].map((val, idx) => (
                  <input
                    key={idx}
                    type="text"
                    inputMode="numeric"
                    maxLength={2}
                    value={val}
                    onChange={e => handleChange(b.key, idx, e.target.value)}
                    className="w-full h-9 text-center font-mono font-bold text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-colors"
                    placeholder="·"
                    data-testid={`input-${b.key}-${idx}`}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Results */}
        {results && (
          <div className="space-y-4" data-testid="results">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Trophy size={22} weight="fill" className="text-[#002FA7]" />
                <span className="font-bold text-lg text-gray-900" data-testid="results-heading">
                  Best Combinations ({(results.combinations || []).length} of up to 200)
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span>{results.total_valid_ball_combos.toLocaleString()} ball combos</span>
                <span>×</span>
                <span>{results.total_valid_ls_combos} LS combos</span>
                <span>=</span>
                <span className="font-bold">{results.total_evaluated.toLocaleString()} evaluated</span>
              </div>
            </div>

            {/* Consensus filter/rerank controls */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white rounded-lg px-4 py-3 flex flex-wrap items-center gap-3" data-testid="pm-consensus-filter">
              <span className="text-xs font-bold">🚦 Consensus Filter:</span>
              {[["OFF","Off"],["MAX_1_RED","Max 1 RED ⭐"],["NO_RED","No RED"],["ONLY_GREEN","Only GREEN"],["RERANK","Re-rank"]].map(([k,l]) => (
                <button key={k} onClick={() => setConsensusMode(k)} className={`text-xs px-2.5 py-1 rounded-full font-bold border ${consensusMode === k ? 'bg-white text-slate-800 border-white' : 'bg-transparent text-white border-white/30 hover:border-white'}`} data-testid={`pm-filter-${k}`}>{l}</button>
              ))}
              <div className="ml-auto flex items-center gap-3">
                <GroupLegend groups={consensus.groups} />
                <ConsensusRefLink className="text-white hover:text-amber-300" />
              </div>
            </div>

            {(() => {
              const displayed = applyConsensusFilter(results.combinations || [], consensusMode, consensus);

              if (displayed.length === 0 && (results.combinations || []).length > 0) {
                return <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center text-xs text-amber-700" data-testid="no-combos-filter">No combinations match the Consensus filter — try "Off" or "Max 1 RED".</div>;
              }

              return displayed.map((combo, ci) => {
              const cmix = combo._quality.mix;
              return (
              <div key={ci} className={`bg-white border ${ci === 0 ? 'border-2 border-yellow-400 ring-2 ring-yellow-200' : 'border-gray-200'} rounded-lg overflow-hidden`} data-testid={`combo-${ci}`}>
                <div className={`bg-gradient-to-r ${verdictStyle[combo.verdict] || verdictStyle.FAIR} px-5 py-3 flex items-center justify-between`}>
                  <div className="flex items-center gap-4">
                    <span className={`rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg ${ci === 0 ? 'bg-yellow-400 text-gray-900' : 'bg-white/20 text-white'}`}>#{ci + 1}</span>
                    <QualityBadge tier={combo._quality.tier} score={combo._quality.score} size="sm" />
                    {ci === 0 && <span className="bg-yellow-400 text-gray-900 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded">Best</span>}
                    <div className="flex items-center gap-2">
                      {combo.balls.map((n, ni) => {
                        const g = consensus.groupOf(n);
                        const gs = GROUP_STYLE[g];
                        return (
                          <span key={ni} className={`inline-flex w-10 h-10 rounded-full bg-white/20 backdrop-blur items-center justify-center font-mono font-bold text-white text-lg ring-2 ring-offset-1 ring-offset-transparent ${gs.ring}`} title={`${g} · Consensus ${consensus.scoreOf(n) >= 0 ? '+' : ''}${consensus.scoreOf(n)}`}>{n}</span>
                        );
                      })}
                      <span className="text-white/50 mx-1">+</span>
                      {combo.lucky_stars.map((n, ni) => (
                        <span key={ni} className="inline-flex w-10 h-10 rounded-full bg-yellow-400/30 backdrop-blur items-center justify-center font-mono font-bold text-yellow-100 text-lg">{n}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1" data-testid={`combo-${ci}-mix`}>
                      {["GREEN","YELLOW","RED"].map(gk => (
                        <span key={gk} className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-white/20 text-white`}>{gk[0]}:{cmix[gk]}</span>
                      ))}
                    </div>
                    <div className="text-right">
                      <div className="text-white/70 text-[10px] uppercase tracking-wider font-bold">Score</div>
                      <div className="text-white font-bold text-2xl font-mono">{combo.score}{consensusMode === 'RERANK' && <span className="block text-[9px] font-normal text-white/70">+{combo._quality.boost.toFixed(1)} bst</span>}</div>
                    </div>
                    <div className="bg-white/20 rounded-lg px-3 py-1.5 text-center">
                      <div className="text-white font-bold text-sm">{combo.verdict}</div>
                      <div className="text-white/70 text-[10px]">{combo.passed}P {combo.warnings}W {combo.failed}F</div>
                    </div>
                  </div>
                </div>
                <div className="px-5 py-3">
                  <div className="flex flex-wrap gap-2">
                    {combo.checks.map((ch, chi) => {
                      const Icon = statusIcon[ch.status] || Info;
                      return (
                        <div key={chi} className="flex items-center gap-1 px-2 py-1 rounded bg-gray-50 border border-gray-100" data-testid={`combo-${ci}-check-${chi}`}>
                          <Icon size={13} weight="fill" className={statusColor[ch.status]} />
                          <span className="text-[10px] font-bold text-gray-600">{ch.name}</span>
                          {ch.value !== undefined && <span className="text-[10px] font-mono text-gray-500">{ch.value}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              );
              });
            })()}

            {results.combinations.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-5 py-8 text-center" data-testid="no-combos">
                <Warning size={32} className="text-amber-500 mx-auto mb-2" />
                <p className="font-bold text-amber-800">No valid combinations found</p>
                <p className="text-sm text-amber-600 mt-1">Try adding more candidate numbers or adjusting your choices</p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
